//------------------------------------------------------ externos
import { useMemo, useState } from "react";
import { createFilter } from "react-select";
import Select from "../inputs/SelectBase";
import {
  FaArrowAltCircleUp as LogoUp,
  FaArrowAltCircleDown as LogoDown,
} from "react-icons/fa";
//------------------------------------------------------ elementos
import ItemCard from "../cards/ItemCard";
import DeleteButton from "../buttons/DeleteButton";
import CrearArticulo from "./CrearArticulo";
//------------------------------------------------------ funciones
import {
  borradorDeArticulo, estanTodas, firma, herramientasSinArticulo, indiceDeStock,
  mismaDescripcion, normal, ofrecerEntre, palabrasDe, pedidasDe, rotuloDeArticulo,
} from "../../shared/stockDesdeTablero";
//------------------------------------------------------ estilos
import "../formularios/css/Forms.css";
import DoubleBooleanButton from "../buttons/DoubleBooleanButton";
import TextButton from "../buttons/TextButton";

/* Cuántas letras hacen falta para ofrecer «Crear … en Stock». Con menos, la
 * opción aparecería apenas se empieza a escribir, antes de que la lista tenga
 * chance de encontrar lo que ya existe. */
const LETRAS_PARA_CREAR = 3;

const filtroComun = createFilter();

const FormList = ({
  articulos = [],
  value = [],
  onChange,
  mostrarTipoMovimiento = true,
  priceActive = false, // para que puede ser simple listado para stock o con precios para facturas
  /* ── EL PUENTE CON EL TABLERO (Nico, 20/10) ─────────────────────────────
   * Con `onCrearArticulo`, lo que Stock no tiene deja de ser «Sin opciones»:
   * si está en el tablero de herramientas se ofrece desde ahí, y si no está en
   * ningún lado se ofrece crearlo. Sin él —el movimiento entre depósitos, que
   * mueve lo que ya hay— la lista es la de siempre, con un cartel que dice qué
   * hacer. Ver `shared/stockDesdeTablero`. */
  onCrearArticulo = null,
  herramientas = [],
  panoles = {},
  herramientasError = "",
  deposito = "",
  tipos = [],
  unidades = [],
  /* Lo que dice la lista cuando no queda ninguna opción. Las pantallas que no
   * lo pasan (reparaciones, asignaciones) siguen con el «Sin opciones» de
   * siempre: con el filtro letra por letra, decir «No está en Stock» sería
   * afirmar de más (FormReparacion.test). */
  sinStockTexto = "Sin opciones",
}) => {
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState("");
  const [valor, setValor] = useState("");
  const [moneda, setMoneda] = useState("AR$");
  const [tipoMovimiento, setTipoMovimiento] = useState("ALTA");
  const [texto, setTexto] = useState("");
  const [borrador, setBorrador] = useState(null);
  const [creado, setCreado] = useState(null);

  /* En una BAJA no se ofrece crear nada: un artículo recién nacido no tiene
     qué sacar, y crearlo para restarle lo dejaría en negativo en el depósito.
     La página del pañol de FlotaBot hace lo mismo («tablerosacar»). */
  const conTablero = typeof onCrearArticulo === "function";
  const puente = conTablero && tipoMovimiento !== "BAJA";
  const sacando = conTablero && tipoMovimiento === "BAJA";

  const opcionesStock = useMemo(
    () =>
      articulos.map((art) => ({
        value: art.value,
        label: `${art.label}`,
        unidad: art.raw?.unidad,
      })),
    [articulos],
  );
  // Las filas de `stock` tal cual, para saber qué herramienta ya tiene artículo.
  const filas = useMemo(() => articulos.map((a) => a.raw || a), [articulos]);

  /* ── LO CARO, UNA VEZ; EN CADA TECLA, LO CORTO (verificación (2), 20/10) ──
   * Qué herramientas del tablero no tienen artículo es comparar el tablero
   * contra todo Stock (35 × 1.842 en producción, ~800 ms). Se hacía en cada
   * render, o sea en cada letra: escribir «guantes de tela» costaba 14 s de
   * CPU contra 1,9 s sin el puente, y la lista se trababa. Eso, las firmas del
   * padrón y las palabras de cada rótulo sólo cambian cuando cambian las
   * listas (o se crea un artículo), así que se calculan entonces. En cada
   * tecla queda filtrar las pocas que no tienen artículo y buscar en un Set.
   * FormList.test lo fija: escribir 15 letras no vuelve a leer Stock. */
  const indice = useMemo(
    () => (conTablero ? indiceDeStock(filas) : null),
    [conTablero, filas],
  );
  const sinArticulo = useMemo(
    () => (conTablero ? herramientasSinArticulo(herramientas, filas, indice) : []),
    [conTablero, herramientas, filas, indice],
  );
  const palabrasStock = useMemo(
    () => (conTablero ? new Map(opcionesStock.map((o) => [o, palabrasDe(o.label)])) : null),
    [conTablero, opcionesStock],
  );
  const pedidas = useMemo(() => pedidasDe(texto), [texto]);

  const ofrecidas = puente ? ofrecerEntre(sinArticulo, texto, { deposito }) : [];
  /* «Crear» no se ofrece si lo escrito ya es, con otras mayúsculas, sin
   * tildes, en plural o con un «de» de más, un artículo o una herramienta de
   * la lista: crearía el duplicado. «guantes de tela» es «Guante Tela». */
  const yaEsta =
    puente &&
    (indice.firmas.has(firma(texto)) ||
      ofrecidas.some(
        (h) => mismaDescripcion(h.nombre, texto) || mismaDescripcion(h.codigo, texto),
      ));
  const puedeCrear =
    puente && normal(texto).length >= LETRAS_PARA_CREAR && !yaEsta;

  const nombreDelPanol = (h) => panoles[h?.panol] || h?.panol || "";

  const opciones = [
    ...opcionesStock,
    ...(ofrecidas.length
      ? [{
          label: "En el tablero de herramientas, todavía no en Stock",
          options: ofrecidas.map((h) => ({
            value: `tablero:${h.id}`,
            label: [h.nombre, nombreDelPanol(h), "crear en Stock"]
              .filter(Boolean)
              .join(" · "),
            herramienta: h,
          })),
        }]
      : []),
    ...(puedeCrear
      ? [{ value: "nuevo", label: `Crear «${texto.trim()}» en Stock`, nuevo: true }]
      : []),
  ];

  /* Las del tablero y «Crear» ya vienen filtradas por lo escrito (por
   * palabras, sin tildes): el filtro de la lista las dejaría afuera cuando el
   * rótulo no contiene el texto letra por letra.
   *
   * Con el puente, lo que YA está en Stock también se encuentra por palabras
   * (`nombra`: raíz, sin las vacías, en cualquier orden), además del filtro de
   * siempre. Sin eso, una vez creado Guante Tela, «guantes de tela» no lo
   * traía: `yaEsta` lo reconocía y no ofrecía crear otro, y la lista decía
   * «No está en Stock» (verificación del 20/10). Sin el puente —las otras
   * pantallas que usan esta lista— queda el filtro de siempre. Las palabras
   * de cada rótulo y las de lo escrito ya vienen armadas: esto corre una vez
   * por artículo en cada tecla. */
  const filtrar = (op, input) => {
    if (op.data?.herramienta || op.data?.nuevo) return true;
    if (filtroComun(op, input)) return true;
    if (!conTablero) return false;
    return estanTodas(
      input === texto ? pedidas : pedidasDe(input),
      palabrasStock.get(op.data) || palabrasDe(op.label),
    );
  };

  /* Los avisos dicen lo que pasó con lo escrito, no que el artículo no
   * existe: la búsqueda no adivina sinónimos ni errores de tipeo. */
  const sinOpciones = ({ inputValue }) => {
    if (sacando) {
      return "Con lo escrito no aparece en Stock, así que no hay qué sacar. Si es nuevo, primero cargalo con ALTA.";
    }
    if (!puente) return sinStockTexto;
    return normal(inputValue).length < LETRAS_PARA_CREAR
      ? "Escribí un poco más: si no está, te ofrece crearlo."
      : sinStockTexto;
  };

  const elegir = (op) => {
    setCreado(null);
    if (op?.herramienta) {
      setBorrador(
        borradorDeArticulo({
          herramienta: op.herramienta,
          panolNombre: nombreDelPanol(op.herramienta),
        }),
      );
      return;
    }
    if (op?.nuevo) {
      setBorrador(borradorDeArticulo({ texto }));
      return;
    }
    setBorrador(null);
    setArticuloSeleccionado(op);
  };

  const crear = async (b) => {
    const r = await onCrearArticulo(b);
    if (r?.ok) {
      const a = r.articulo;
      setArticuloSeleccionado({ value: a.id, label: rotuloDeArticulo(a), unidad: a.unidad });
      setBorrador(null);
      setCreado(a);
    }
    return r;
  };

  //------------------------------------------------------ agregar
  const handleAgregar = () => {
    if (!articuloSeleccionado || !cantidad) return;

    const nuevoArticulo = {
      id: articuloSeleccionado.value,
      descripcion: articuloSeleccionado.label,
      cantidad:
        tipoMovimiento === "BAJA"
          ? -Math.abs(Number(cantidad))
          : Number(cantidad),
      unidad: articuloSeleccionado.unidad || "UNIDADES",
      valor: Number(valor) || 0,
      moneda,
      tipo: tipoMovimiento,
    };

    onChange([...value, nuevoArticulo]);

    setArticuloSeleccionado(null);
    setCantidad("");
    setValor("");
    setMoneda("AR$");
    setCreado(null);
  };

  const handleEliminar = (index) => {
    const updated = value.filter((_, i) => i !== index);

    onChange(updated);
  };

  return (
    <div className="form-box-doble">
      {mostrarTipoMovimiento && (
        <>
          <DoubleBooleanButton
            optionA={"ALTA"}
            optionB={"BAJA"}
            state={tipoMovimiento}
            setState={setTipoMovimiento}
          />
        </>
      )}

      <div className="form-row">
        <div className="select-container">
          <label htmlFor="seleccionar-item">
            <strong>Seleccionar Ítem</strong>
          </label>

          <Select
            inputId="seleccionar-item"
            className="select-grow"
            options={opciones}
            value={articuloSeleccionado}
            onChange={elegir}
            onInputChange={(v) => setTexto(v)}
            filterOption={filtrar}
            placeholder="Seleccionar artículo..."
            noOptionsMessage={sinOpciones}
            isClearable
          />
          {conTablero && herramientasError && (
            <p className="crear-articulo-nota">
              No se pudo leer el tablero de herramientas: lo que esté cargado
              sólo ahí no aparece en esta lista.
            </p>
          )}
        </div>
      </div>

      {borrador && puente && (
        <CrearArticulo
          borrador={borrador}
          setBorrador={setBorrador}
          tipos={tipos}
          unidades={unidades}
          sinTablero={!!herramientasError}
          panolNombre={nombreDelPanol(borrador.herramienta)}
          onCrear={crear}
          onCancelar={() => setBorrador(null)}
        />
      )}
      {creado && (
        <p className="crear-articulo-listo" role="status">
          «{creado.descripcion}» quedó en Stock como {creado.id} y ya está
          elegido. Poné la cantidad y tocá +Agregar.
        </p>
      )}

      {priceActive && (
        <label>
          <strong>Costo / Valor</strong>
          <div className="form-inline">
            <input
              type="number"
              className="form-input"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
            <DoubleBooleanButton
              optionA={"AR$"}
              optionB={"U$D"}
              state={moneda}
              setState={setMoneda}
            />
          </div>
        </label>
      )}

      <div className="form-row">
        <div>
          <strong>Cantidad</strong>
          <input
            type="number"
            className="form-input"
            value={cantidad}
            aria-label="Cantidad"
            onChange={(e) => setCantidad(e.target.value)}
          />
        </div>
        <div className="centrar">
          <TextButton
            text="+Agregar"
            mini={true}
            onClick={handleAgregar}
            type={"button"}
          />
        </div>
      </div>

      {value.length > 0 && (
        <ul className="form-box">
          {value.map((item, index) => (
            <li key={index} className="list-item">
              <ItemCard
                logo={item.cantidad < 0 ? <LogoDown /> : <LogoUp />}
                logoColor={`${item.cantidad < 0 ? "var(--crit, #920e0e)" : "#0a8d15c0"}`}
                title={`${item.descripcion}`}
                txt1={item.id}
                txt2={`${item.cantidad} ${priceActive ? `(${item.moneda}${item.valor})` : ""}`}
              />
              <DeleteButton onClose={() => handleEliminar(index)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FormList;
